//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useEffect, useState, useCallback, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext } from '../contexts';
import { DEFAULT_CURRENCY_SYMBOL } from '../constants';
import {
    getIsExactRatePlaceholder,
    getRateMinPlaceholder,
    getRateMaxPlaceholder,
    PLACEHOLDER_FIELD_RATE_PLUS,
    PLACEHOLDER_FIELD_RATE_MAX2,
    PLACEHOLDER_FIELD_PAY_FREQUENCY,
} from '../placeholderHelper';
import { setClassNameInDefns } from '../definitionHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdNumberFormat from './AfitdNumberFormat';
import AfitdTextField from './AfitdTextField';
import AfitdAutocompleteLite from './AfitdAutocompleteLite';
import AfitdIconButton from './AfitdIconButton';
import AfitdTypography from './AfitdTypography';
import AfitdCheckbox from './AfitdCheckbox';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { rateMaxIsHidden, payFrequency } = params;

    return makeStyles(theme => ({
        root: {
            border: '1px solid transparent',
            padding: '0, 10px',
        },
        radioGroup: {
            display: 'flex',
            alignItems: 'center',
        },
        rateMin: {
            marginBottom: rateMaxIsHidden ? undefined : 10,
        },
        rateMinSingle: {
            marginTop: 5,
        },
        IconButtonContainer: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginTop: '5px',
        },
        rateContainer: {
            margin: '0 10px 0 10px',
        },
        payFrequencyContainer: {
            margin: '10px 10px 0 10px',
        },
        checkboxContainer: { marginTop: 10, display: 'flex', alignItems: 'center' },
        showOnlyText: {
            color: !!payFrequency ? undefined : '#9B9E9C',
        },
        clearButton: {
            padding: '5px',
        },
        closeIcon: {
            color: 'rgba(0, 0, 0, 0.54)',
            fontSize: '18px',
        },
        submitButton: {
            padding: '5px',
            marginRight: '10px',
        },
        checkIcon: {
            color: 'rgba(0, 0, 0, 0.54)',
            fontSize: '18px',
        },
    }));
};

//---------------------------------------------------------------------
// Component specific constants
//---------------------------------------------------------------------
const RADIO_EXACT = 'exact';
const RADIO_RANGE = 'range';
const ERROR_INVALID_RANGE = 'Min cannot be greater than Max';
const VARIANT_RANGE = 'range';
const VARIANT_SINGLE = 'single';
const VARIANT_SINGLE_MAX = 'single-max';

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        appContext,
        rateMin,
        rateMax,
        payFrequency,
        currencySymbol,
        rateMinPlaceholder,
        rateMaxPlaceholder,
        rateMinError,
        rateMaxError,
        rateMaxIsHidden,
        showOnly,
        getRateMinMaxHelperText,
        handleChangeRateMin,
        handleChangeRateMax,
        handleChangeRateMinSingle,
        handleChangeRateMaxSingle,
        handleChangePayFrequency,
        handleChangeShowOnly,
        isInvalidRange,
    } = params;
    const { lovs } = appContext;
    const { isValid } = props;

    const payFrequencyLov = _get(lovs, 'payFrequencyLov', []);
    const defns = {
        rateMin: {
            placeholder: rateMinPlaceholder,
            value: rateMin,
            thousandSeparator: true,
            onValueChange: handleChangeRateMin,
            error: isInvalidRange || rateMinError || !isValid,
            helperText: getRateMinMaxHelperText(),
            prefix: currencySymbol || DEFAULT_CURRENCY_SYMBOL,
        },
        rateMax: {
            placeholder: rateMaxPlaceholder,
            value: rateMax,
            thousandSeparator: true,
            isHidden: rateMaxIsHidden,
            onValueChange: handleChangeRateMax,
            error: isInvalidRange || rateMaxError || !isValid,
            helperText: getRateMinMaxHelperText(),
            prefix: currencySymbol || DEFAULT_CURRENCY_SYMBOL,
            // isAllowed: maxRateChecker,
        },
        rateMinSingle: {
            placeholder: PLACEHOLDER_FIELD_RATE_PLUS,
            value: rateMin,
            thousandSeparator: true,
            onValueChange: handleChangeRateMinSingle,
            error: rateMinError || !isValid,
            // helperText={getRateMinMaxHelperText()}
            prefix: currencySymbol || DEFAULT_CURRENCY_SYMBOL,
            suffix: '+',
        },
        rateMaxSingle: {
            placeholder: PLACEHOLDER_FIELD_RATE_MAX2,
            value: rateMax,
            thousandSeparator: true,
            onValueChange: handleChangeRateMaxSingle,
            error: rateMaxError || !isValid,
            // helperText={getRateMinMaxHelperText()}
            prefix: currencySymbol || DEFAULT_CURRENCY_SYMBOL,
            suffix: ' (max)',
        },
        payFrequency: {
            options: payFrequencyLov,
            placeholder: PLACEHOLDER_FIELD_PAY_FREQUENCY,
            value: payFrequency,
            error: !isValid,
            onChange: handleChangePayFrequency,
        },
        showOnly: {
            checked: showOnly,
            onChange: handleChangeShowOnly,
            disabled: !!!payFrequency,
        },
        showOnlyText: {
            variant: 'body1',
            component: 'span',
            value: `Show only ${payFrequency || ''}`,
        },
    };
    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
// build object. Set rateMax to rateMin if exact
const getPayload = (isExactRate, rateMin, rateMax) => ({
    isExactRate,
    rateMin,
    rateMax: isExactRate ? rateMin : rateMax,
});

const validateRateRange = (payload, setIsInvalidRange, setIsValid) => {
    const { isExactRate, rateMin, rateMax } = payload;

    if (!isExactRate) {
        const isInvalidRange = !_isNil(rateMin) && !_isNil(rateMax) && rateMin > rateMax;
        setIsInvalidRange(isInvalidRange); // set local state
        setIsValid && setIsValid(!isInvalidRange); // tell the parent about this error
    }
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const AfitdSalaryRateFilter = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    // const { currentUser, userId, profileData, currentEmployer, employerId, lovs } = appContext;

    // const layoutContext = useContext(LayoutContext);
    // const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const {
        isHidden,
        isExactRate,
        rateMin: rateMinProp,
        rateMax: rateMaxProp,
        payFrequency: payFrequencyProp,
        rateMinError,
        rateMaxError,
        currencySymbol = '$',
        setIsValid,
        // onChange,
        // maxValue,
        onSubmit,
        onClear,
        isButtonHidden,
        // ...otherProps,
        variant = 'range', // range, single
    } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [activeRadio, setActiveRadio] = useState(null); //isExactRate ? RADIO_EXACT : RADIO_RANGE);
    const [rateMin, setRateMin] = useState(null);
    const [rateMax, setRateMax] = useState(null);
    const [payFrequency, setPayFrequency] = useState(null);
    const [showOnly, setShowOnly] = useState(false);
    const [isInvalidRange, setIsInvalidRange] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        setRateMin(rateMinProp);
    }, [rateMinProp]);

    useEffect(() => {
        setRateMax(rateMaxProp);
    }, [rateMaxProp]);

    useEffect(() => {
        setActiveRadio(isExactRate ? RADIO_EXACT : RADIO_RANGE);
    }, [isExactRate]);

    useEffect(() => {
        setPayFrequency(payFrequencyProp);
    }, [payFrequencyProp]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const variantRange = variant === VARIANT_RANGE;
    const variantSingle = variant === VARIANT_SINGLE;
    const variantSingleMax = variant === VARIANT_SINGLE_MAX;
    const rateMaxIsHidden = activeRadio === RADIO_EXACT || !variantRange;
    const rateMinPlaceholder = activeRadio === RADIO_EXACT ? getIsExactRatePlaceholder(currencySymbol) : getRateMinPlaceholder(currencySymbol);
    const rateMaxPlaceholder = getRateMaxPlaceholder(currencySymbol);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    // Max Limit Cap.
    // const maxRateChecker = inputObj => {
    //     const { value } = inputObj;
    //     if (value <= maxValue) return true;
    //     return false;
    // };

    const submitWithValue = useCallback(
        (valueObj = {}) => {
            onSubmit && onSubmit({ isExactRate: activeRadio === RADIO_EXACT, rateMin, rateMax, payFrequency, currencySymbol, showOnly, ...valueObj });
        },
        [activeRadio, rateMin, rateMax, payFrequency, currencySymbol, showOnly, onSubmit],
    );

    const handleChangeRateRadio = useCallback(evt => {
        const value = evt.target.value;
        setActiveRadio(value);
    }, []);

    const handleChangeRateMin = useCallback(
        value => {
            const rateMinValue = value.floatValue;
            const isExactChecked = activeRadio === RADIO_EXACT;
            const payload = getPayload(isExactChecked, rateMinValue, rateMax);
            validateRateRange(payload, setIsInvalidRange, setIsValid);
            setRateMin(rateMinValue);
        },
        [activeRadio, rateMax, setIsValid],
    );

    const handleChangeRateMax = useCallback(
        value => {
            const rateMaxValue = value.floatValue;
            const isExactChecked = activeRadio === RADIO_EXACT;
            const payload = getPayload(isExactChecked, rateMin, rateMaxValue);
            validateRateRange(payload, setIsInvalidRange, setIsValid);
            setRateMax(rateMaxValue);
        },
        [activeRadio, rateMin, setIsValid],
    );

    const handleChangeRateMinSingle = useCallback(
        value => {
            const rateValue = value.floatValue;
            setRateMin(rateValue);
            isButtonHidden && submitWithValue({ rateMin: rateValue });
        },
        [isButtonHidden, submitWithValue],
    );

    const handleChangeRateMaxSingle = useCallback(
        value => {
            const rateValue = value.floatValue;
            setRateMax(rateValue);
            isButtonHidden && submitWithValue({ rateMax: rateValue });
        },
        [isButtonHidden, submitWithValue],
    );

    const handleChangePayFrequency = useCallback(
        (evt, option) => {
            setPayFrequency(option);
            isButtonHidden && submitWithValue({ payFrequency: option });
        },
        [isButtonHidden, submitWithValue],
    );

    const handleChangeShowOnly = useCallback(
        evt => {
            setShowOnly(evt.target.checked);
            isButtonHidden && submitWithValue({ showOnly: evt.target.checked });
        },
        [isButtonHidden, submitWithValue],
    );

    const getRateMinMaxHelperText = useCallback(() => {
        return isInvalidRange ? ERROR_INVALID_RANGE : '';
    }, [isInvalidRange]);

    const handleClickClearButton = () => {
        setRateMin('');
        setRateMax('');
        setPayFrequency(null);
        setShowOnly(null);
        onClear && onClear();
    };

    const handleClickSaveButton = () => {
        onSubmit && onSubmit({ isExactRate: activeRadio === RADIO_EXACT, rateMin, rateMax, payFrequency, currencySymbol, showOnly });
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ rateMaxIsHidden, payFrequency })();
    const defns = getDefns({
        classes,
        props,
        appContext,
        rateMin,
        rateMax,
        payFrequency,
        currencySymbol,
        rateMinPlaceholder,
        rateMaxPlaceholder,
        rateMinError,
        rateMaxError,
        rateMaxIsHidden,
        showOnly,
        getRateMinMaxHelperText,
        handleChangeRateMin,
        handleChangeRateMax,
        handleChangeRateMinSingle,
        handleChangeRateMaxSingle,
        handleChangePayFrequency,
        handleChangeShowOnly,
        isInvalidRange,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    if (isHidden) return null;
    else if (variantRange)
        return (
            <div {...defns.root}>
                <div {...defns.radioGroup}>
                    <RadioGroup row value={activeRadio} onChange={handleChangeRateRadio}>
                        <FormControlLabel control={<Radio />} value={RADIO_EXACT} label="Exact" />
                        <FormControlLabel control={<Radio />} value={RADIO_RANGE} label="Range" />
                    </RadioGroup>
                </div>
                <div>
                    <AfitdNumberFormat customInput={AfitdTextField} {...defns.rateMin} />
                    <AfitdNumberFormat customInput={AfitdTextField} {...defns.rateMax} />
                    {!isButtonHidden && (
                        <div {...defns.IconButtonContainer}>
                            <AfitdIconButton {...defns.submitButton} onClick={handleClickSaveButton}>
                                <CheckIcon {...defns.checkIcon} />
                            </AfitdIconButton>
                            <AfitdIconButton {...defns.clearButton} onClick={handleClickClearButton}>
                                <CloseIcon {...defns.closeIcon} />
                            </AfitdIconButton>
                        </div>
                    )}
                </div>
            </div>
        );
    else if (variantSingle || variantSingleMax)
        return (
            <div {...defns.root}>
                <div>
                    <div {...defns.rateContainer}>
                        {!variantSingleMax && <AfitdNumberFormat customInput={AfitdTextField} {...defns.rateMinSingle} />}
                        {variantSingleMax && <AfitdNumberFormat customInput={AfitdTextField} {...defns.rateMaxSingle} />}
                    </div>
                    <div {...defns.payFrequencyContainer}>
                        <AfitdTypography {...defns.label}>Pay Frequency</AfitdTypography>
                        <AfitdAutocompleteLite {...defns.payFrequency} />
                    </div>
                    <div {...defns.checkboxContainer}>
                        <AfitdCheckbox {...defns.showOnly} />
                        <AfitdTypography {...defns.showOnlyText} />
                    </div>
                    {!isButtonHidden && (
                        <div {...defns.IconButtonContainer}>
                            <AfitdIconButton {...defns.submitButton} onClick={handleClickSaveButton}>
                                <CheckIcon {...defns.checkIcon} />
                            </AfitdIconButton>
                            <AfitdIconButton {...defns.clearButton} onClick={handleClickClearButton}>
                                <CloseIcon {...defns.closeIcon} />
                            </AfitdIconButton>
                        </div>
                    )}
                </div>
            </div>
        );
};

export default AfitdSalaryRateFilter;
